/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { PIQuestionData, PIReport, PIReportResponse, PISummary } from '../../models/pi/pi.model';
import { ObservableResult } from '../../store';
import { RestClientService } from '../rest-client.service';
import { PIDataService } from './pi-data.service';

/**
 * Callers that leave `languageCode` out fall back to the interceptor's default, which is the
 * language the app itself is displayed in.
 */
function languageHeaders(languageCode?: string): { [name: string]: string } | null {
  return languageCode ? { 'Accept-Language': languageCode } : null;
}

@Injectable()
export class ApiPIDataService implements PIDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  loadPISummary(resourceUid: string): ObservableResult<PISummary> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `pi/${resourceUid}/summary`);
    return this.client.get<PISummary>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetPISummary'))),
    );
  }

  getPIQuestion(resourceUid: string, version: number, languageCode?: string): ObservableResult<PIQuestionData> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `pi/${resourceUid}/cursor/${version}`);
    return this.client.post<PIQuestionData>(url, null, null, languageHeaders(languageCode)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetPIQuestion'))),
    );
  }

  addPIQuestionAnswer(
    resourceUid: string,
    version: number,
    request: PIQuestionData,
    languageCode?: string,
  ): ObservableResult<PIQuestionData> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `pi/${resourceUid}/cursor/${version}`);
    return this.client.post<PIQuestionData>(url, request, null, languageHeaders(languageCode)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetPIQuestion'))),
    );
  }

  getPIReports(resourceUid: string, languageCode?: string): ObservableResult<PIReportResponse> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `pi/${resourceUid}/reports`);
    return this.client.get<PIReportResponse>(url, null, languageHeaders(languageCode)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetPIReports'))),
    );
  }

  getPIReportByVersion(resourceUid: string, version: number, languageCode?: string): ObservableResult<PIReport> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `pi/${resourceUid}/reports/${version}`);
    return this.client.get<PIReport>(url, null, languageHeaders(languageCode)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetPIReport'))),
    );
  }
}
